/**
 * Audio Processing Utilities
 * Helper functions for audio format conversion and microphone configuration
 * These run in the main thread (not in the worklet)
 */

// Microphone Configuration Helper
export const getMicrophoneConstraints = (deviceId?: string): MediaStreamConstraints => {
  const audioConstraints: MediaTrackConstraints = {
    sampleRate: 24000, // Match Azure's expected rate
    channelCount: 1, // Mono audio
    echoCancellation: true, // Enable echo cancellation
    noiseSuppression: true, // Enable noise suppression
    autoGainControl: true, // Automatic volume adjustment
  };

  // Add device ID if specified
  if (deviceId) {
    audioConstraints.deviceId = { exact: deviceId };
  }

  return {
    audio: audioConstraints,
  };
};

// Helper function to initialize microphone with proper constraints
export const initializeMicrophone = async (deviceId?: string): Promise<MediaStream> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(getMicrophoneConstraints(deviceId));
    return stream;
  } catch (error) {
    console.error('Error accessing microphone:', error);
    throw new Error('Failed to access microphone. Please ensure microphone permissions are granted.');
  }
};

// Helper function to convert Int16Array to Base64 (for sending to server)
export const int16ArrayToBase64 = (int16Array: Int16Array): string => {
  // Respect byteOffset/byteLength in case int16Array is a subarray of a larger buffer
  const uint8Array = new Uint8Array(
    int16Array.buffer,
    int16Array.byteOffset,
    int16Array.byteLength
  );
  // Collect into an array then join once to avoid O(n²) string concatenation in hot audio loop
  const chars = new Array<string>(uint8Array.length);
  for (let i = 0; i < uint8Array.length; i++) {
    chars[i] = String.fromCharCode(uint8Array[i]);
  }
  return btoa(chars.join(''));
};

// Helper function to convert hex string to Int16Array (for receiving from server)
export const hexToInt16Array = (hexString: string): Int16Array => {
  const bytes = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    bytes[i / 2] = parseInt(hexString.substr(i, 2), 16);
  }
  return new Int16Array(bytes.buffer);
};

// Helper function to convert Int16Array to Float32Array for playback
export const int16ToFloat32 = (int16Array: Int16Array): Float32Array => {
  const float32Array = new Float32Array(int16Array.length);
  for (let i = 0; i < int16Array.length; i++) {
    // Convert Int16 (-32768 to 32767) to Float32 (-1.0 to 1.0)
    float32Array[i] = int16Array[i] / 32768.0;
  }
  return float32Array;
};

// Audio playback helper
export class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private sampleRate: number = 24000;

  constructor(sampleRate: number = 24000) {
    this.sampleRate = sampleRate;
  }

  async initialize(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
    }
    
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  async playAudio(int16Array: Int16Array): Promise<void> {
    if (!this.audioContext) {
      await this.initialize();
    }

    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    const float32Array = int16ToFloat32(int16Array);
    const audioBuffer = this.audioContext.createBuffer(1, float32Array.length, this.sampleRate);
    // Create a new Float32Array with ArrayBuffer to satisfy type requirements
    const channelData = new Float32Array(float32Array.buffer as ArrayBuffer);
    audioBuffer.copyToChannel(channelData, 0);

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.audioContext.destination);
    source.start();
  }

  close(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
